import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { uploadFileToServer, createTaskCourseMaterial } from '../../service';

/** The material exists server-side, but attaching it to its draft was refused. */
class AttachError extends Error {}

// Mirrors the server-side extraction tolerance (the parse path allows 25 MB); the
// design mock said 5mb, but a real syllabus extract PDF routinely lands at 5–8 MB.
const MAX_BYTES = 25 * 1024 * 1024;

// Extension is the authority for both acceptance and MIME type. Drag-and-drop
// bypasses the input's accept filter entirely, and browsers hand over an empty
// file.type for perfectly valid .doc/.docx files — inferring from the extension
// keeps an unsupported file from being uploaded at all, and keeps a Word document
// from being mislabelled application/pdf for the server-side extractor.
const MIME_BY_EXTENSION = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};
const ACCEPTED = Object.keys(MIME_BY_EXTENSION)
  .map((extension) => `.${extension}`)
  .join(',');
const extensionOf = (name) => (name || '').split('.').pop().toLowerCase();

/**
 * "Add course materials" — the task-level attach modal from the Create Task design.
 *
 * Two tabs. Upload: files are uploaded and saved AS THEY COMPLETE (each becomes its own
 * material, named after the file), so a teacher adding three files sees three rows appear
 * on the card without babysitting a batch. Write: free text saved on Continue.
 *
 * There is deliberately no review step and no subject picker — this is the teacher's own
 * teaching content for THIS task, sent to JeddAI as-is. The curated curriculum catalog is
 * a separate, automatic path.
 */
export const AddCourseMaterialsModal = ({
  onClose,
  onSaved,
  onSaveStarted,
  // Called once per started save when it has finished, with whether it succeeded — the
  // counterpart to onSaveStarted (whose return value, if any, is handed back to onSaved
  // as a second argument so the caller can tell which save a material came from).
  onSaveSettled,
  remainingCapacity = Infinity,
}) => {
  const [tab, setTab] = useState('upload');
  const [files, setFiles] = useState([]);
  const [writeTitle, setWriteTitle] = useState('');
  const [writeText, setWriteText] = useState('');
  const [savingWrite, setSavingWrite] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const savingCount = files.filter((f) => f.status === 'saving').length + (savingWrite ? 1 : 0);

  const handleFiles = (fileList) => {
    setError(null);
    // The server caps a task at 8 attachments; refusing here keeps a big batch from
    // creating materials the save would then reject. Count IN-FLIGHT saves only:
    // completed ones already shrank remainingCapacity via the parent card, so counting
    // them again would refuse valid files below the limit.
    const inFlight = files.filter((f) => f.status === 'saving').length;
    let capacity = remainingCapacity - inFlight;
    Array.from(fileList || []).forEach((file) => {
      if (capacity <= 0) {
        setError('A task can carry at most 8 attached course materials.');
        return;
      }
      if (!MIME_BY_EXTENSION[extensionOf(file.name)]) {
        setError(
          `${file.name} isn't a supported file type. Use PDF, Word (.doc/.docx), PNG or JPEG — or paste the content into the Write tab.`,
        );
        return;
      }
      if (file.size > MAX_BYTES) {
        setError(
          `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 25 MB.`,
        );
        return;
      }
      capacity -= 1;
      const entry = { key: `${file.name}-${Date.now()}-${Math.random()}`, name: file.name, status: 'saving' };
      // Dirty the editor BEFORE the async save: browser Back would otherwise slip past
      // the unsaved-changes guard while the material is still being created server-side.
      const token = onSaveStarted?.();
      setFiles((prev) => [...prev, entry]);
      saveFile(file, entry.key, token);
    });
  };

  const saveFile = async (file, key, token) => {
    let succeeded = false;
    const finish = (status) =>
      setFiles((prev) => prev.map((f) => (f.key === key ? { ...f, status } : f)));
    try {
      const url = await uploadFileToServer(file);
      if (!url) throw new Error('upload failed');
      const material = await createTaskCourseMaterial({
        title: file.name.replace(/\.[^.]+$/, ''),
        fileUrl: url,
        mimeType: extensionOf(file.name) === 'docx' || /\.pdf(?:[?#]|$)/i.test(url)
          ? 'application/pdf' : MIME_BY_EXTENSION[extensionOf(file.name)],
      });
      // postApi resolves null on failure — null is an error, never an empty success.
      if (!material || !material.id) throw new Error('save failed');
      // "Added" means attached, not merely created: on My Drafts the attach is its own
      // request, and a file that landed server-side but never reached the draft would
      // otherwise show as done with its error hidden behind this modal.
      if ((await onSaved?.(material, token)) === false) throw new AttachError();
      succeeded = true;
      finish('done');
    } catch (e) {
      finish('error');
      setError(
        e instanceof AttachError
          ? `We saved ${file.name} but couldn't attach it. Try again.`
          : `We couldn't read ${file.name}. Try a text-based PDF or Word file, or paste the content into the Write tab.`,
      );
    } finally {
      onSaveSettled?.(succeeded);
    }
  };

  const handleContinue = async () => {
    if (savingCount > 0) return;
    if (tab === 'write' && writeText.trim()) {
      if (remainingCapacity - files.filter((f) => f.status === 'saving').length <= 0) {
        setError('A task can carry at most 8 attached course materials.');
        return;
      }
      const token = onSaveStarted?.();
      setSavingWrite(true);
      setError(null);
      let succeeded = false;
      try {
        const material = await createTaskCourseMaterial({
          title: writeTitle.trim() || 'Course materials',
          text: writeText,
        });
        if (!material || !material.id) {
          setError("We couldn't save that content. Try again.");
          return;
        }
        if ((await onSaved?.(material, token)) === false) {
          setError("We saved that content but couldn't attach it. Try again.");
          return;
        }
        succeeded = true;
      } finally {
        setSavingWrite(false);
        onSaveSettled?.(succeeded);
      }
    }
    onClose?.();
  };

  // Portalled to <body>: the modal is a fixed overlay, and rendering it inside whatever
  // opened it exposes it to ancestor descendant-selectors (the response submit wrapper
  // paints every nested button purple) and to that ancestor's stacking context.
  // The portal moves the DOM node, not the React tree: synthetic events from inside the
  // modal still bubble to whatever rendered it — on My Drafts that is the response
  // submit wrapper, whose click handler opens the marking-preferences popup.
  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label="Add course materials"
      onClick={(event) => event.stopPropagation()}
    >
      <Container>
        <Header>
          <Title>Add course materials</Title>
          <HeaderActions>
            {savingCount > 0 && <SavingHint>⟳ Saving</SavingHint>}
            {/* Closing mid-save would let the teacher save/leave the task before the
                material lands — created server-side but never attached. The header
                already says "Saving"; the close unlocks the moment it finishes. */}
            <CloseButton
              type="button"
              aria-label="Close"
              disabled={savingCount > 0}
              onClick={() => {
                if (savingCount === 0) onClose?.();
              }}
            >
              ✕
            </CloseButton>
          </HeaderActions>
        </Header>

        <Tabs>
          <Tab type="button" $active={tab === 'upload'} onClick={() => setTab('upload')}>
            Upload
          </Tab>
          <Tab type="button" $active={tab === 'write'} onClick={() => setTab('write')}>
            Write
          </Tab>
        </Tabs>

        {tab === 'upload' ? (
          <DropZone
            $dragOver={dragOver}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer?.files);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED}
              multiple
              hidden
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <SelectFilesButton type="button" onClick={() => inputRef.current?.click()}>
              Select Files
            </SelectFilesButton>
            <DropHint>Or drop files here</DropHint>
            <LimitHint>(Maximum file upload size: 25mb)</LimitHint>

            {files.length > 0 && (
              <FileList>
                {files.map((f) => (
                  <FileRow key={f.key} $status={f.status}>
                    <span>{f.name}</span>
                    <FileStatus $status={f.status}>
                      {f.status === 'saving' && 'Saving…'}
                      {f.status === 'done' && 'Added'}
                      {f.status === 'error' && 'Failed'}
                    </FileStatus>
                  </FileRow>
                ))}
              </FileList>
            )}
          </DropZone>
        ) : (
          <WriteArea>
            <TitleInput
              value={writeTitle}
              onChange={(e) => setWriteTitle(e.target.value)}
              placeholder="Title (optional)"
              aria-label="Course material title"
            />
            <TextArea
              value={writeText}
              onChange={(e) => setWriteText(e.target.value)}
              placeholder="Write or paste the teaching and learning content JeddAI should prioritise for this task…"
              aria-label="Course material content"
            />
          </WriteArea>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <Footer>
          <ContinueButton
            type="button"
            onClick={handleContinue}
            disabled={savingCount > 0 || (tab === 'write' && !writeText.trim() && files.length === 0)}
          >
            Continue
          </ContinueButton>
        </Footer>
      </Container>
    </Overlay>,
    document.body,
  );
};

const Overlay = styled.div`
  position: fixed;
  z-index: 9999;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  background-color: #fff;
  width: 70%;
  min-width: 520px;
  max-width: 1000px;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  font-family: var(--font-family-ibm_plex_sans);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const SavingHint = styled.span`
  font-size: 14px;
  color: #6f6a76;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text, #1d1929);
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  font-size: 16px;
  color: #1d1929;
  cursor: pointer;
  padding: 4px;

  &:disabled {
    color: #c9c6cc;
    cursor: not-allowed;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 24px;
  padding: 0 20px;
  border-bottom: 1px solid #eceaee;
`;

const Tab = styled.button`
  border: none;
  background: none;
  padding: 10px 0 12px;
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active }) => ($active ? '#7200e0' : '#6f6a76')};
  border-bottom: 2px solid ${({ $active }) => ($active ? '#7200e0' : 'transparent')};
  cursor: pointer;
`;

const DropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 320px;
  padding: 24px;
  background: ${({ $dragOver }) => ($dragOver ? '#f9f3ff' : '#fff')};
`;

const SelectFilesButton = styled.button`
  border: 1px solid #7200e0;
  border-radius: 24px;
  background: #fff;
  color: #7200e0;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 26px;
  cursor: pointer;

  &:hover {
    background: #f9f3ff;
  }
`;

const DropHint = styled.div`
  font-size: 14px;
  color: #6f6a76;
`;

const LimitHint = styled.div`
  font-size: 12px;
  color: #9a95a1;
`;

const FileList = styled.div`
  width: 100%;
  max-width: 560px;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FileRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: var(--text, #1d1929);
  border: 1px solid #eceaee;
  border-radius: 8px;
  padding: 8px 12px;
  opacity: ${({ $status }) => ($status === 'error' ? 0.7 : 1)};
`;

const FileStatus = styled.span`
  flex-shrink: 0;
  color: ${({ $status }) =>
    $status === 'error' ? '#c62828' : $status === 'done' ? '#2e7d32' : '#6f6a76'};
`;

const WriteArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  min-height: 320px;
`;

const TitleInput = styled.input`
  border: 1px solid #d8d2df;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  flex: 1;
  min-height: 220px;
  border: 1px solid #d8d2df;
  border-radius: 8px;
  padding: 12px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
`;

const ErrorText = styled.div`
  padding: 0 20px 8px;
  font-size: 13px;
  color: #c62828;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  background: #f7f6f8;
`;

const ContinueButton = styled.button`
  border: none;
  border-radius: 20px;
  background: #7200e0;
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 22px;
  cursor: pointer;

  &:disabled {
    background: #c9b7e2;
    cursor: not-allowed;
  }
`;

export default AddCourseMaterialsModal;
